import React, { useEffect, useState } from 'react'
import { Transition } from '@headlessui/react'
import classNames from 'classnames'
import { ResponsiveBackgroundImage } from '@/atoms/ResponsiveBackgroundImage'
import { BackgroundPosition } from '../types'

interface ResponsiveBackgroundContainerProps {
  cloudinaryImgPath: string
  cloudinaryTabletImgPath?: string
  cloudinaryMobileImgPath?: string
  theaterBackgroundPosition?: BackgroundPosition
  contentRef?: React.RefObject<HTMLDivElement | null>
  projectView?: boolean
  darkenBackground?: boolean
}

export const ResponsiveBackgroundContainer = React.forwardRef<HTMLDivElement, ResponsiveBackgroundContainerProps>(
  (props, ref) => {
    const {
      cloudinaryImgPath,
      cloudinaryMobileImgPath,
      cloudinaryTabletImgPath,
      theaterBackgroundPosition,
      contentRef,
      projectView = false,
      darkenBackground,
    } = props
    const [contentHeight, setContentHeight] = useState<number | null>(null)

    useEffect(() => {
      const calculateHeight = () => {
        if (contentRef?.current) {
          const height = contentRef.current.getBoundingClientRect().height
          setContentHeight(height)
        }
      }

      calculateHeight()

      window.addEventListener('resize', calculateHeight)

      return () => {
        window.removeEventListener('resize', calculateHeight)
      }
    }, [contentRef])

    return (
      <Transition
        show
        appear
        as="div"
        enter="transition-opacity duration-1000"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="transition-opacity duration-5500"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div
          ref={ref}
          className={classNames(
            'pointer-events-none top-0 left-0 z-0 w-full',
            projectView
              ? 'fixed min-h-[56vh] md:min-h-[60vh] lg:min-h-[70vh] xl:min-h-[80vh] 2xl:min-h-[630px] 2xl:max-h-[630px] overflow-y-hidden transition-opacity duration-300 ease-in-out will-change-opacity'
              : 'absolute min-h-[56vh] md:min-h-[60vh] lg:min-h-[70vh] xl:min-h-[80vh] 2xl:min-h-[630px] 2xl:max-h-[630px]',
            darkenBackground && projectView ? 'opacity-25' : 'opacity-100',
          )}
          style={
            contentHeight
              ? {
                  height: contentHeight,
                  minHeight: contentHeight,
                }
              : undefined
          }
        >
          <div className="block lg:hidden">
            <div className="block md:hidden">
              {/* Mobile image */}
              <ResponsiveBackgroundImage
                src={cloudinaryMobileImgPath ?? cloudinaryImgPath}
                className="aspect-h-1 aspect-w-1 block h-fit w-full bg-center object-cover"
                transforms="g_face,c_fill"
              />
            </div>
            <div className="hidden md:block lg:hidden">
              {/* Tablet image */}
              <ResponsiveBackgroundImage
                src={cloudinaryTabletImgPath ?? cloudinaryImgPath}
                className="bg-top-right aspect-h-9 aspect-w-16 block w-full bg-cover"
                transforms="g_north_east,c_fill"
              />
            </div>
          </div>
          <div className="hidden pb-12 lg:block">
            {/* Desktop image */}
            <ResponsiveBackgroundImage
              src={cloudinaryImgPath}
              className={classNames(
                'h-full w-full bg-top-right aspect-w-16 aspect-h-9 absolute top-0 right-0',
                theaterBackgroundPosition,
              )}
              transforms="g_face,c_fill"
            />
          </div>
          {/* Left -> Right Gradient */}
          <div
            className={classNames(
              'hidden md:block absolute inset-0 left-0 top-0 h-full min-w-1/2 xl:min-w-[60%] transition-opacity',
            )}
            style={{
              background:
                'linear-gradient(90deg, rgba(21, 21, 21, 0.9) 0%, rgba(21, 21, 21, 0.7) 34.24%, rgba(21, 21, 21, 0) 69.25%)',
            }}
          />
          {/* Top -> Bottom Gradient */}
          <div
            className={classNames('absolute inset-0 left-0 top-0 min-h-1/6 w-full transition-opacity')}
            style={{
              background: 'linear-gradient(180deg, #151515 0%, rgba(21, 21, 21, 0) 30%)',
              height: contentHeight ? contentHeight + 150 : '80vh',
            }}
          />
          {/* Bottom -> Top Gradient Mobile */}
          <div
            className={classNames(
              'absolute inset-0 left-0 bottom-0 h-fit aspect-w-1 aspect-h-1 w-full mt-10 transition-opacity md:hidden',
            )}
            style={{
              background: 'linear-gradient(0deg, #141414 28.15%, rgba(21, 21, 21, 0) 70%)',
            }}
          />
          {/* Bottom -> Top Gradient Tablet */}
          <div
            className={classNames(
              'absolute inset-0 left-0 bottom-0 min-h-1/2 w-full transition-opacity hidden md:block lg:hidden',
            )}
            style={{
              background: 'linear-gradient(0deg, #141414 28.15%, rgba(21, 21, 21, 0) 70%)',
              height: contentHeight ? contentHeight + 150 : '80vh',
            }}
          />
          {/* Bottom -> Top Gradient Desktop */}
          <div
            className={classNames(
              'absolute inset-0 left-0 bottom-0 min-h-1/2 w-full transition-opacity hidden lg:block',
            )}
            style={{
              background: 'linear-gradient(0deg, #141414 18.15%, rgba(21, 21, 21, 0) 70%)',
              height: contentHeight ? contentHeight + 150 : '80vh',
            }}
          />
        </div>
      </Transition>
    )
  },
)

ResponsiveBackgroundContainer.displayName = 'ResponsiveBackgroundContainer'
